<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import Inbox from "./inbox.vue";
import emailContentEditor from "../_v2EmailContentEditor.vue";
import Schedule from "./schedule.vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'
const notificationStore = useNotificationStore()

const props = defineProps({
    automationId: {
        type: Number,
        required: true,
    }
})

const currentStep = ref(null)
const automation = ref(null)
const editingName = ref(false)
const contentValidationIssues = ref([])
const state = ref('')
const showSendPreviewEmailModal = ref(false)
const previewEmailAddress = ref('')
const copying = ref(false)

const recipientCounts = reactive({
    total: 0,
    email: 0,
    phone: 0,
})

const loadAutomationData = async () => {
    if (props.automationId === null) {
        return
    }
    const res = await axios.get(route('api.bo.crm.automation.show', {automation_id: props.automationId}))

    automation.value = res.data.automation;
    automation.value.email_template.body = JSON.stringify([]);
}

const save = async () => {
    if (automation.value === null) {
        return
    }
    if (! scheduleValidates.value) {
        return
    }
    state.value = 'saving'

    const res = await axios.put(route('api.bo.crm.automation.update', automation.value.id), automation.value)
    state.value = 'success'

    automation.value.is_active = res.data.automation.is_active
}

const onContentUpdate = (newContent) => {
    automation.value.email_template.body = newContent
}

const onContentValidation = (validationIssues) => {
    contentValidationIssues.value = validationIssues
}

const onIncludedTagsSelected = (tags) => {
    automation.value.included_lead_tags = tags
}

const onExcludedTagsSelected = (tags) => {
    automation.value.excluded_lead_tags = tags
}

const loadRecipientCount = async () => {
    if (! automation.value?.included_lead_tag_ids) {
        return;
    }

    const res = await axios.get(route('crm.leadtags.count'), {
        params: {
            included_tags: automation.value.included_lead_tag_ids,
            excluded_tags: automation.value.excluded_lead_tag_ids,
            once_assigned_in_time_range_start: automation.value.once_assigned_in_time_range_start,
            once_assigned_in_time_range_end: automation.value.once_assigned_in_time_range_end,
            delivery_types: ['email'],
        }
    });

    recipientCounts.total = res.data.leads_count
    recipientCounts.email = res.data.leads_with_email_count
    recipientCounts.phone = res.data.leads_with_phone_count
}

const sendPreviewEmail = async (email) => {
    if (email === 'myself') {
        email = window.myflow.user.email
    }

    const res = await axios.post(route('crm.communications.automations.dispatchTestMessages', automation.value.id), {
        email: email
    })

    if (res.data.success) {
        notificationStore.addNotification({
            type: 'success',
            message: _mft('communication:composer.previewEmailSent'),
            dismissAfter: 2500,
        })
    }
}

const updateAutomationName = async () => {
    if (automation.value.name.length === 0) {
        return;
    }

    const res = await axios.put(route('api.bo.crm.automation.updateName', automation.value.id), {
        name: automation.value.name
    })

    if (res.data.success) {
        editingName.value = false
    }
}

const copyAutomation = async () => {
    if (copying.value === true) {
        return;
    }
    copying.value = true
    const res = await axios.post(route('crm.communications.automations.clone', automation.value.id))

    if (res.data.success) {
        window.location.href = route('bo.crm.automations.edit', res.data.automation_id)
    }
}

const inboxValidates = computed(() => {
    return automation.value?.email_template?.subject?.length > 0
})

const contentValidates = computed(() => {
    return automation.value?.email_template.body.length > 0 && contentValidationIssues.value.length === 0
})

const recipientsValidates = computed(() => {
    if (automation.value.send_rule === 'once_assigned_in_time_range') {
        return automation.value?.included_lead_tag_ids?.length > 0 &&
            automation.value?.once_assigned_in_time_range_start &&
            automation.value?.once_assigned_in_time_range_start
    }

    return automation.value?.included_lead_tag_ids?.length > 0
})

const scheduleValidates = computed(() => {
    if (automation.value.schedule_type !== 'schedule') {
        return true
    }

    return automation.value.activate_at &&
        new Date(automation.value.activate_at) - new Date() >= -1 * 60 * 1000
})

const canBeScheduled = computed(() => {
    return inboxValidates.value && recipientsValidates.value && contentValidates.value
})

const scheduleIsSet = computed(() => {
    return automation.value.schedule_type === 'send_immediately' ||
        (automation.value.schedule_type === 'schedule' && automation.value.activate_at)
})

const saveButtonLabel = computed(() => {
    if (automation.value.schedule_type === 'send_immediately') {
        if (automation.value.is_active) {
            return _mft('communication:automation.save')
        } else {
            return _mft('communication:automation.saveAndActivate')
        }
    }
    if (automation.value.schedule_type === 'schedule') {
        return _mft('communication:automation.saveAndSchedule')
    }

    return _mft('communication:automation.save')
})

watch(() => automation.value?.included_lead_tags, (tags) => {
    automation.value.included_lead_tag_ids = tags.map(tag => tag.id)
    loadRecipientCount()
}, {deep: true})

watch(() => automation.value?.excluded_lead_tags, (tags) => {
    automation.value.excluded_lead_tag_ids = tags.map(tag => tag.id)
    loadRecipientCount()
}, {deep: true})

onMounted(() => {
    loadAutomationData()
})

</script>
<template>
    <div>
        <div v-if="automation">
            <div class="md:sticky top-2 z-topbar">
                <frosted-bar>
                    <div class="flex items-center justify-between space-x-4 f">
                        <div class="flex space-x-1 items-start">
                            <h1>
                                {{ automation ? automation.name : '' }}
                            </h1>
                            <button-transparent
                                size="xs"
                                @click="editingName = true"
                            >
                                <i class="fa-regular fa-pen text-xs text-gray-400"></i>
                            </button-transparent>
                        </div>

                        <div class="flex items-center space-x-4">
                            <button-more-options
                                :label="_mft('communication:composer.preview')"
                                alignment="right"
                                :cancel-on-outside-click="false"
                            >
                                <button-more-options-option
                                    @click=""
                                >
                                    <a
                                        :href="route('crm.communications.automations.preview', automation.id)"
                                        target="_blank"
                                        class="whitespace-nowrap"
                                    >
                                        {{ _mft('communication:previewInBrowser') }}
                                    </a>
                                </button-more-options-option>
                                <button-more-options-option>
                                    <span
                                        @click="sendPreviewEmail('myself')"
                                        class="whitespace-nowrap"
                                    >
                                        {{ _mft('communication:sendPreviewToSelf') }}
                                    </span>
                                </button-more-options-option>
                                <button-more-options-option
                                    @click="showSendPreviewEmailModal = true"
                                >
                                    <span class="whitespace-nowrap">
                                        {{ _mft('communication:sendPreviewToSomeoneElse') }}
                                    </span>
                                </button-more-options-option>
                            </button-more-options>

                            <button-secondary
                                @click="copyAutomation"
                                :disabled="copying"
                            >{{ _mft('communication:copyToNewCommunication') }}</button-secondary>

                            <button-success
                                @click="save"
                                :state="state"
                                :disabled="! scheduleValidates"
                            >
                                {{
                                    saveButtonLabel
                                }}
                            </button-success>
                        </div>
                    </div>

                    <template #navigation>
                        <a
                            :href="route('bo.crm.automations.list')"
                            class="mf-link text-xs"
                        >
                            &larr; {{ _mft('communication:automation.domainNamePlural') }}
                        </a>
                    </template>
                </frosted-bar>
            </div>


            <div class="mt-8">
                <ui-stepped
                    :track-state="true"
                    :display-progress-bar="true"
                    @changestatusstep="currentStep = $event"
                >
                    <ui-stepped-step
                        title="Inbox"
                        value="inbox"
                        :completed="inboxValidates"
                        :unlocked="true"
                        :has-warning="!inboxValidates"
                        :active="true"
                    >
                        <div class="grid lg:grid-cols-2 gap-8 mt-12">
                            <div>
                                <h2 class="mb-2">{{ _mft('communication:composer.inbox.content') }}</h2>
                                <inbox
                                    :automation="automation"
                                />
                            </div>
                            <div>
                                <div>
                                    <h2 class="mb-2">{{ _mft('communication:composer.inbox.preview') }}</h2>
                                    <communications-inbox-preview
                                        :newsletter="automation"
                                    ></communications-inbox-preview>
                                </div>
                            </div>
                        </div>
                    </ui-stepped-step>
                    <ui-stepped-step
                        :title="_mft('communication:composer.steps.content')"
                        value="content"
                        :unlocked="true"
                        :has-warning="!contentValidates"
                        :completed="contentValidates"
                    >
                        <emailContentEditor
                            v-if="automation?.email_template"
                            class="mt-12"
                            ref="emailContentEditorRef"
                            :content="automation.email_template.block_editor_content.content"
                            :automationId="automation.id"
                            @update:emailContent="onContentUpdate"
                            @update:validation="onContentValidation"
                        />
                    </ui-stepped-step>
                    <ui-stepped-step
                        :title="_mft('communication:composer.steps.tags')"
                        value="recipients"
                        :completed="recipientsValidates"
                        :unlocked="true"
                    >
                        <h2 class="mt-12 mb-2">{{ _mft('communication:composer.steps.tags') }}</h2>
                        <div
                            class="grid md:grid-cols-2 gap-8"
                        >
                            <div>
                                <input-label :label="'Tags that run this automation'"></input-label>
                                <p class="text-xs text-gray-500 mb-1">
                                    {{ _mft('communication:recurringAfterTagAssigned.prefix') }} {{ [0, NaN].includes(automation.recurring_after_tag_assigned_delay_in_hours) ? _mft('communication:recurringAfterTagAssigned.directlyWhen') : automation.recurring_after_tag_assigned_delay_in_hours + ' ' + _mft('communication:recurringAfterTagAssigned.hoursAfter') }} {{ _mft('communication:recurringAfterTagAssigned.suffix') }}
                                </p>
                                <tags-multi-select-v2
                                    :allow-creation="false"
                                    :placeholder="_mft('crm:searchTags')"
                                    :pre-selected-tags="automation?.included_lead_tags.map(tag => tag.id) || []"
                                    :display-as="'list'"
                                    :display-counts="['email']"
                                    @tagsSelected="onIncludedTagsSelected"
                                />
                            </div>
                            <div>
                                <input-label :label="'Tags that prevent this automation'"></input-label>
                                <p class="text-xs text-gray-500 mb-1">
                                    {{ _mft('communication:recurringAfterTagAssigned.exludedIfAssigned') }}
                                </p>
                                <tags-multi-select-v2
                                    :allow-creation="false"
                                    :placeholder="_mft('crm:searchTags')"
                                    :pre-selected-tags="automation?.excluded_lead_tags.map(tag => tag.id) || []"
                                    :display-as="'list'"
                                    :display-counts="['email']"
                                    @tagsSelected="onExcludedTagsSelected"
                                />
                            </div>

                            <div>
                                <input-number
                                    class="mb-1"
                                    :content="automation.recurring_after_tag_assigned_delay_in_hours"
                                    @update:content="automation.recurring_after_tag_assigned_delay_in_hours = $event"
                                    :label="_mft('communication:recurringAfterTagAssignedDelayInHours')"
                                    :show-step-ui="true"
                                    :step="1"
                                    min="0"
                                    max="720"
                                />
                                <div class="flex flex-wrap -mt-2">
                                    <button-base
                                        size="sm"
                                        :style-type="automation.recurring_after_tag_assigned_delay_in_hours === 0 ? 'secondary' : 'base'"
                                        class="mr-1 mb-1"
                                        @click="automation.recurring_after_tag_assigned_delay_in_hours = 0"
                                    >
                                        {{ _mft('communication:delay.immediate') }}
                                    </button-base>
                                    <button-base
                                        size="sm"
                                        :style-type="automation.recurring_after_tag_assigned_delay_in_hours === 2 ? 'secondary' : 'base'"
                                        class="mr-1 mb-1"
                                        @click="automation.recurring_after_tag_assigned_delay_in_hours = 2"
                                    >
                                        {{ _mft('communication:delay.twoHours') }}
                                    </button-base>
                                    <button-base
                                        size="sm"
                                        :style-type="automation.recurring_after_tag_assigned_delay_in_hours === 24 ? 'secondary' : 'base'"
                                        class="mr-1 mb-1"
                                        @click="automation.recurring_after_tag_assigned_delay_in_hours = 24"
                                    >
                                        {{ _mft('communication:delay.oneDay') }}
                                    </button-base>
                                    <button-base
                                        size="sm"
                                        :style-type="automation.recurring_after_tag_assigned_delay_in_hours === 48 ? 'secondary' : 'base'"
                                        class="mr-1 mb-1"
                                        @click="automation.recurring_after_tag_assigned_delay_in_hours = 48"
                                    >
                                        {{ _mft('communication:delay.twoDays') }}
                                    </button-base>
                                    <button-base
                                        size="sm"
                                        :style-type="automation.recurring_after_tag_assigned_delay_in_hours === 168 ? 'secondary' : 'base'"
                                        class="mr-1 mb-1"
                                        @click="automation.recurring_after_tag_assigned_delay_in_hours = 168"
                                    >
                                        {{ _mft('communication:delay.oneWeek') }}
                                    </button-base>
                                    <button-base
                                        size="sm"
                                        :style-type="automation.recurring_after_tag_assigned_delay_in_hours === 336 ? 'secondary' : 'base'"
                                        class="mr-1 mb-1"
                                        @click="automation.recurring_after_tag_assigned_delay_in_hours = 336"
                                    >
                                        {{ _mft('communication:delay.twoWeeks') }}
                                    </button-base>
                                    <button-base
                                        size="sm"
                                        :style-type="automation.recurring_after_tag_assigned_delay_in_hours === 720 ? 'secondary' : 'base'"
                                        class="mr-1 mb-1"
                                        @click="automation.recurring_after_tag_assigned_delay_in_hours = 720"
                                    >
                                        {{ _mft('communication:delay.thirtyDays') }}
                                    </button-base>
                                </div>
                            </div>
                        </div>

                    </ui-stepped-step>
                    <ui-stepped-step
                        :title="_mft('communication:composer.summary.automation.schedule')"
                        value="schedule"
                        :unlocked="canBeScheduled"
                        :completed="scheduleIsSet"
                        :tooltip="! canBeScheduled ? _mft('communication:completeStepsToSchedule') : null"
                    >
                        <h2 class="mt-12 mb-2">{{ _mft('communication:composer.summary.automation.schedule.title') }}</h2>
                        <schedule
                            :automation="automation"
                        />
                        <div
                            v-if="! scheduleValidates"
                            class="text-right text-myflowRed"
                        >
                            {{ _mft('communication:composer.summary.automation.scheduleToSave') }}
                        </div>
                    </ui-stepped-step>

                    <template #action-right-global>
                        <button-success
                            v-if="currentStep === 'schedule'"
                            @click="save"
                            :disabled="! scheduleValidates"
                            :state="state"
                        >
                            {{
                                saveButtonLabel
                            }}
                        </button-success>
                    </template>

                </ui-stepped>
            </div>

            <modal-basic
                :open="showSendPreviewEmailModal"
            >
                <template #title>
                    {{ _mft('communication:sendPreviewEmail') }}
                </template>
                <template #body>
                    <div>
                        <div class="space-y-4">
                            <input-text
                                :label="_mft('communication:sendPreviewEmail.recipientEmail')"
                                v-model:content="previewEmailAddress"
                            ></input-text>
                        </div>
                        <div class="flex items-center justify-end space-x-4 mt-4">
                            <button-base
                                @click="showSendPreviewEmailModal = false"
                            >
                                {{ _mft('shared:cancel') }}
                            </button-base>
                            <button-primary
                                :disabled="! previewEmailAddress || previewEmailAddress.length === 0"
                                @click="sendPreviewEmail(previewEmailAddress); showSendPreviewEmailModal = false"
                            >
                                {{ _mft('communication:sendPreview') }}
                            </button-primary>
                        </div>
                    </div>
                </template>
            </modal-basic>

            <modal-basic
                :open="editingName"
            >
                <template #title>
                    {{ _mft('communication:changeName') }}
                </template>
                <template #body>
                    <div>
                        <div class="space-y-4">
                            <input-text
                                :label="_mft('communication:automationName')"
                                maxlength="250"
                                v-model:content="automation.name"
                                autofocus
                            ></input-text>
                        </div>
                        <div class="flex items-center justify-end space-x-4 mt-4">
                            <button-base
                                @click="editingName = false"
                            >
                                {{ _mft('shared:cancel') }}
                            </button-base>
                            <button-primary
                                :disabled="automation.name.length === 0"
                                @click="updateAutomationName"
                            >
                                {{ _mft('shared:action.update') }}
                            </button-primary>
                        </div>
                    </div>
                </template>
            </modal-basic>
        </div>
    </div>
</template>
